import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { generateProblemSet, generateChoices } from '../utils/mathProblems';
import { useTimer } from '../hooks/useTimer';
import { Timer } from '../components/common/Timer';
import { OPERATION_LABEL, OPERATION_COLOR } from '../data/mathFacts';
import '../styles/math.css';

export const MathPracticePage = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const config = state?.config;

  useEffect(() => {
    if (!config) navigate('/math', { replace: true });
  }, [config, navigate]);

  const problems = useMemo(
    () => (config ? generateProblemSet(config) : []),
    [config]
  );

  const [phase, setPhase] = useState('play'); // 'play' | 'complete'
  const [index, setIndex] = useState(0);
  const [typedAnswer, setTypedAnswer] = useState('');
  const [feedback, setFeedback] = useState(null); // null | 'correct' | 'wrong'
  const [selectedChoice, setSelectedChoice] = useState(null);
  const [correctCount, setCorrectCount] = useState(0);
  const [answeredCount, setAnsweredCount] = useState(0);
  const timersRef = useRef([]);
  const inputRef = useRef(null);
  useEffect(() => () => timersRef.current.forEach(clearTimeout), []);

  const timer = useTimer({
    mode: config?.timeLimitMs ? 'countdown' : 'countup',
    durationMs: config?.timeLimitMs || 0,
    autoStart: true,
    onExpire: () => setPhase('complete'),
  });

  const problem = problems[index];
  const choices = useMemo(
    () => (problem && config?.answerMode === 'choice' ? generateChoices(problem) : []),
    [problem, config]
  );

  useEffect(() => {
    if (config?.answerMode === 'type') inputRef.current?.focus();
  }, [index, config]);

  if (!config) return null;

  const advance = () => {
    setTypedAnswer('');
    setFeedback(null);
    setSelectedChoice(null);
    if (index + 1 >= problems.length) {
      timer.pause();
      setPhase('complete');
    } else {
      setIndex(i => i + 1);
    }
  };

  const submitTypedAnswer = () => {
    if (feedback !== null || typedAnswer === '') return;
    const isCorrect = Number(typedAnswer) === problem.answer;
    setAnsweredCount(c => c + 1);
    if (isCorrect) setCorrectCount(c => c + 1);
    setFeedback(isCorrect ? 'correct' : 'wrong');
    timersRef.current.push(setTimeout(advance, 1100));
  };

  const handleChoiceTap = (choice) => {
    if (selectedChoice !== null) return;
    setSelectedChoice(choice);
    const isCorrect = choice === problem.answer;
    setAnsweredCount(c => c + 1);
    if (isCorrect) setCorrectCount(c => c + 1);
    timersRef.current.push(setTimeout(advance, 1000));
  };

  const restart = () => {
    setIndex(0);
    setTypedAnswer('');
    setFeedback(null);
    setSelectedChoice(null);
    setCorrectCount(0);
    setAnsweredCount(0);
    setPhase('play');
    timer.reset();
    timer.start();
  };

  if (phase === 'complete' || !problem) {
    return (
      <div className="math-page math-page--center">
        <div style={{ fontSize: 56, marginBottom: 12 }}>🎉</div>
        <h2 style={{ margin: '0 0 8px' }}>Drill Complete</h2>
        <p style={{ color: 'var(--text-secondary)', marginBottom: 24 }}>
          {correctCount} / {answeredCount} correct · <Timer mode="countup" ms={timer.elapsedMs} />
        </p>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
          <button className="math-back-btn" onClick={restart}>Practice Again</button>
          <button className="math-back-btn" onClick={() => navigate('/math')}>Change Settings</button>
          <button className="math-back-btn math-back-btn--primary" onClick={() => navigate('/math')}>
            Back to Math
          </button>
        </div>
      </div>
    );
  }

  const opColor = OPERATION_COLOR[problem.operation];

  return (
    <div className="math-page">
      <div className="math-header">
        <button className="math-back-btn" onClick={() => navigate('/math')}>← Back</button>
        <span className="math-progress">{index + 1} / {problems.length}</span>
        <span className="math-timer">
          <Timer mode={config.timeLimitMs ? 'countdown' : 'countup'} ms={config.timeLimitMs ? timer.remainingMs : timer.elapsedMs} />
        </span>
      </div>

      <div
        className={`math-flashcard${feedback ? ` math-flashcard--${feedback}` : ''}`}
        style={{ '--op-color': opColor }}
      >
        <span className="math-flashcard-op-badge" style={{ background: opColor }}>
          {OPERATION_LABEL[problem.operation]}
        </span>
        <div className="math-flashcard-prompt">{problem.prompt}</div>

        {config.answerMode === 'type' ? (
          <form
            className="math-answer-form"
            onSubmit={e => { e.preventDefault(); submitTypedAnswer(); }}
          >
            <input
              ref={inputRef}
              className="math-answer-input"
              type="number"
              inputMode="numeric"
              value={typedAnswer}
              disabled={feedback !== null}
              onChange={e => setTypedAnswer(e.target.value)}
              placeholder="?"
            />
            <button
              type="submit"
              className="math-reveal-btn"
              disabled={feedback !== null || typedAnswer === ''}
            >
              Check
            </button>
          </form>
        ) : (
          <div className="math-choice-grid">
            {choices.map(choice => {
              const state =
                selectedChoice === null ? '' :
                choice === problem.answer ? 'math-choice-btn--correct' :
                choice === selectedChoice ? 'math-choice-btn--wrong' : '';
              return (
                <button
                  key={choice}
                  className={`math-choice-btn ${state}`}
                  disabled={selectedChoice !== null}
                  onClick={() => handleChoiceTap(choice)}
                >
                  {choice}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
